import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { IUser } from "../interfaces/users";
import { ISSOProviders } from "../interfaces/SSOProviders";
import { EEntity, EModule } from "../components/table/filter/useTables";
import * as Sentry from "@sentry/react";

import {
  getTokenAndExpirationStatus,
  showUser,
  signIn,
  signOut,
} from "@/services";
// Se o hook estiver em outro caminho, ajuste este import:
import { useSSOProviders } from "@/services";

type AuthAction = "view" | "create" | "delete" | "update";

interface AuthContextType {
  expired: boolean;
  login: (data: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  user: IUser | null;
  providers: ISSOProviders[];
  checkPermission: (
    module: EModule,
    entity: EEntity,
    action?: AuthAction
  ) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { expired } = getTokenAndExpirationStatus();

  const [user, setUser] = useState<IUser | null>(null);
  const [providers, setProviders] = useState<ISSOProviders[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getInfo = useCallback(async () => {
    setLoading(true);
    try {
      const { user: u } = await showUser({ id: "me", includes: ["profile"] });
      setUser(u);
      Sentry.setUser({
        id: u.id,
        email: u.email,
        username: u.name,
      });
      Sentry.setTag("system_name", "newChain-front");
    } finally {
      // pequeno delay para suavizar transição visual
      setTimeout(() => setLoading(false), 300);
    }
  }, []);

  const checkPermission = useCallback(
    (module: EModule, entity: EEntity, action: AuthAction = "view") => {
      // remove prefixo "<module>_" da entity, ex.: "users_list" => "list"
      const key = entity?.replace(`${module?.toLowerCase()}_`, "");
      return Boolean(user?.profile?.modules?.[module]?.[key]?.[action]);
    },
    [user]
  );

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      await signIn({ email, password });
      await getInfo();
      navigate("/towermanagement");
    },
    [getInfo, navigate]
  );

  const logout = useCallback(async () => {
    // navega para home, limpa sessão no backend e volta para tela de login
    navigate("/", { replace: true });
    await signOut();
    Sentry.setUser(null);
    navigate("/auth/signin");
  }, [navigate]);

  // Busca usuário atual se há token válido
  useEffect(() => {
    if (!user && !expired) {
      void getInfo();
    }
  }, [user, expired, getInfo]);

  // Busca provedores SSO
  const ssoQuery = useSSOProviders({ enabled: true });
  useEffect(() => {
    if (ssoQuery.data?.ssoProviders) {
      setProviders(ssoQuery.data.ssoProviders);
    }
  }, [ssoQuery.data?.ssoProviders]);

  const value = useMemo<AuthContextType>(
    () => ({
      expired,
      login,
      logout,
      user,
      providers,
      checkPermission,
    }),
    [expired, login, logout, user, providers, checkPermission]
  );

  return loading ? (
    <CircularProgress />
  ) : (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth deve ser usado dentro de <AuthProvider>.");
  }
  return ctx;
};
